import React, { useEffect, useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';
import Seo from '@/components/Seo';
import NotFound from '@/pages/NotFound';
import {
  stateBySlug, stateLabel, statePath, licenseNote, citySlug, cityNameFromSlug, stateCityPath,
  hasCityPages, CITY_PAGES_PER_STATE, CITY_MONEY_PAGES_PER_STATE, UsState,
} from '@/data/seo/states';
import {
  breadcrumbSchema, faqSchema, serviceSchema, itemListSchema, brandFaqs, Faq, cityEntitySchema, speakableSchema,
} from '@/lib/seoSchema';
import {
  Breadcrumbs, PageHero, AnswerBlock, Section, FaqSection, LinkGrid, CtaBand, EntityFacts, PageWrap, TrustStrip,
} from '@/components/seo/SeoBlocks';
import { LEAD_PRICE, money, TRADES, Lang } from '@/data/constants';
import LiveProjects from '@/components/seo/LiveProjects';
import { statewideMoneyPages, cityMoneyPages } from '@/data/seo/moneyPages';
import { StateAlertForm, TopCitiesSection } from '@/components/seo/StatePageBlocks';
import { fetchCityIndex, CityCount } from '@/lib/stateAlerts';

// ---------------------------------------------------------------------------
// STATE + CITY LANDING PAGE — /en/states/:code/:city | /es/estados/:code/:city
//
// The city list is NOT hand-typed: it is the browse-listings `cityIndex`
// aggregate (names + open-project counts). A city is INDEXABLE while it is in
// the state's top CITY_PAGES_PER_STATE — exactly the set the sitemap emits —
// and links to per-trade lead pages while it is in the top
// CITY_MONEY_PAGES_PER_STATE (/leads/roofing-leads-plano-tx).
// A slug outside that set still renders (old links never 404) but is marked
// noindex so thin pages never enter the index. California redirects to its
// dedicated /california/:city hierarchy.
// ---------------------------------------------------------------------------

const cityFaqs = (city: string, st: UsState, lang: Lang, count: number | null): Faq[] => {
  const stateName = stateLabel(st, lang);
  const place = `${city}, ${st.code}`;
  const price = money(LEAD_PRICE);
  const n = count ?? 0;
  return brandFaqs(
    lang === 'es'
      ? [
          { q: `¿Hay proyectos de construcción abiertos en ${place}?`, a: count != null ? `Sí. GIGGONE tiene ${n} ${n === 1 ? 'proyecto abierto' : 'proyectos abiertos'} en ${city}, ${stateName}, publicados por propietarios y contratistas generales. El oficio, el presupuesto y la ciudad son visibles gratis; la dirección y los contactos se desbloquean con un lead.` : `GIGGONE muestra proyectos abiertos en ${city}, ${stateName}, publicados por propietarios y contratistas generales. El oficio, el presupuesto y la ciudad son visibles gratis.` },
          { q: `¿Cómo consigo trabajo de construcción en ${city}?`, a: `Cree una cuenta de subcontratista, elija sus oficios y desbloquee los proyectos de ${city} con un lead de su membresía o un crédito individual de ${price}. También puede crear una alerta gratuita para ${place}.` },
          { q: `¿Cuánto cuesta un lead de construcción en ${city}?`, a: `${price} por lead, el mismo precio fijo en todo Estados Unidos. La ciudad, el oficio y el presupuesto no cambian el precio.` },
          { q: `¿Necesito licencia de contratista para trabajar en ${city}?`, a: `${licenseNote(st, lang)} ${city} puede exigir además registro municipal o permisos para ciertos trabajos.` },
          { q: `¿Publicar un proyecto en ${city} cuesta algo?`, a: `No. Publicar es gratis para propietarios y contratistas generales en ${city} y en todo Estados Unidos.` },
        ]
      : [
          { q: `Are there open construction projects in ${place}?`, a: count != null ? `Yes. GIGGONE has ${n} open ${n === 1 ? 'project' : 'projects'} in ${city}, ${stateName}, posted by homeowners and general contractors. Trade, budget range and city are free to view; the address and contacts unlock with one lead.` : `GIGGONE lists open projects in ${city}, ${stateName}, posted by homeowners and general contractors. Trade, budget range and city are free to view.` },
          { q: `How do I find construction work in ${city}?`, a: `Create a subcontractor account, pick your trades and unlock ${city} projects with an included membership lead or a ${price} single credit. You can also set a free job alert for ${place}.` },
          { q: `How much does a construction lead cost in ${city}?`, a: `${price} per lead — the same flat price everywhere in the U.S. City, trade and budget never change it.` },
          { q: `Do I need a contractor license to work in ${city}?`, a: `${licenseNote(st, lang)} ${city} may also require local registration or permits for certain work.` },
          { q: `Does it cost anything to post a project in ${city}?`, a: `No. Posting is free for homeowners and general contractors in ${city} and everywhere in the U.S.` },
        ],
  );
};

export const StateCityPage: React.FC = () => {
  const { code = '', city: slug = '' } = useParams();
  const { lang, path, t } = useI18n();
  const st = stateBySlug(code);
  const [top, setTop] = useState<CityCount[] | null | undefined>(undefined);

  useEffect(() => {
    if (!st || !hasCityPages(st)) return;
    let alive = true;
    setTop(undefined);
    // Same `cityIndex` aggregate as the sitemap and the per-trade city lead
    // pages, so "indexable" and "has trade lead pages" always agree everywhere.
    fetchCityIndex(CITY_PAGES_PER_STATE).then((idx) => alive && setTop(idx ? idx[st.code] || [] : null));
    return () => {
      alive = false;
    };
  }, [st?.code]);

  if (!st || !slug) return <NotFound />;
  if (!hasCityPages(st)) return <Navigate to={path(`/california/${citySlug(slug)}`)} replace />;

  const match = top?.find((c) => citySlug(c.city) === citySlug(slug));
  const city = match?.city || cityNameFromSlug(slug);
  // Index only cities currently in the state's top list (the sitemap set).
  const indexable = top !== undefined && !!match;
  const stateName = stateLabel(st, lang);
  const place = `${city}, ${st.code}`;
  const url = path(stateCityPath(st, city));
  const faqs = cityFaqs(city, st, lang, match ? match.count : null);
  const es = lang === 'es';

  const intro = es
    ? `Proyectos de construcción activos en ${city}, ${stateName}. Propietarios y contratistas generales publican gratis; los contratistas desbloquean la dirección exacta, los contactos y los planos por ${money(LEAD_PRICE)} por lead.`
    : `Active construction projects in ${city}, ${stateName}. Homeowners and general contractors post for free; contractors unlock the exact address, contacts and plan documents for a flat ${money(LEAD_PRICE)} per lead.`;

  const tradeLinks = TRADES.map((tr) => ({
    label: es ? `${tr.es} en ${city}` : `${tr.en} in ${city}`,
    to: `${path('/jobs')}?state=${st.code}&city=${encodeURIComponent(city)}&trade=${tr.slug}`,
  }));
  const leadLinks = statewideMoneyPages(st.code).map((m) => ({
    label: es ? `Leads de ${m.trade.es} en ${stateName}` : `${m.trade.en} Leads in ${stateName}`,
    to: path(`/leads/${m.slug}`),
  }));
  // Per-trade city lead pages exist only for the state's top
  // CITY_MONEY_PAGES_PER_STATE cities (same ranked aggregate) — never link to
  // a noindexed page.
  const rank = top ? top.findIndex((c) => citySlug(c.city) === citySlug(slug)) : -1;
  const cityLeadLinks =
    rank >= 0 && rank < CITY_MONEY_PAGES_PER_STATE
      ? cityMoneyPages(st, city).map((m) => ({
          label: es ? `Leads de ${m.trade.es} en ${place}` : `${m.trade.en} Leads in ${place}`,
          to: path(`/leads/${m.slug}`),
        }))
      : [];

  return (
    <>
      <Seo
        title={es ? `Proyectos y Leads de Construcción en ${place}` : `${place} Construction Projects & Contractor Leads`}
        description={
          es
            ? `Proyectos de construcción reales en ${city}, ${stateName}: todos los oficios, leads a ${money(LEAD_PRICE)} y alertas gratis de nuevos proyectos.`
            : `Real construction projects in ${city}, ${stateName} — every trade, flat ${money(LEAD_PRICE)} leads and free alerts for new ${city} projects.`
        }
        noindex={!indexable}
        schema={[
          breadcrumbSchema([
            { name: 'Home', url: `/${lang}` },
            { name: es ? 'Estados' : 'States', url: path('/states') },
            { name: stateName, url: path(statePath(st)) },
            { name: city, url },
          ]),
          cityEntitySchema(city, st),
          serviceSchema({ trade: 'Construction project leads', area: `${city}, ${st.en}`, description: intro, url }),
          faqSchema(faqs),
          itemListSchema(`${city}, ${st.code} construction trades`, tradeLinks.map((m) => ({ name: m.label, url: m.to }))),
          speakableSchema(url, place),
        ]}
      />

      <PageHero
        eyebrow={es ? `${stateName} · Ciudad` : `${stateName} · City`}
        title={es ? `Construcción en ${place}` : `${place} Construction Projects`}
        intro={intro}
      >
        <Link to={`${path('/jobs')}?state=${st.code}&city=${encodeURIComponent(city)}`} className="rounded-xl bg-orange-500 px-5 py-3 text-sm font-bold hover:bg-orange-600">
          {es ? `Ver proyectos en ${city}` : `Browse ${city} projects`}
        </Link>
        <a href="#alerts" className="rounded-xl border border-white/20 px-5 py-3 text-sm font-bold hover:bg-white/10">
          {es ? 'Crear alerta gratis' : 'Get free alerts'}
        </a>
      </PageHero>

      <PageWrap>
        <Breadcrumbs
          items={[
            { name: t('nav.home'), url: path('/') },
            { name: es ? 'Estados' : 'States', url: path('/states') },
            { name: stateName, url: path(statePath(st)) },
            { name: city },
          ]}
        />

        <AnswerBlock question={faqs[0].q} answer={faqs[0].a} />

        <EntityFacts
          rows={[
            { k: es ? 'Ciudad' : 'City', v: place },
            { k: es ? 'Proyectos abiertos' : 'Open projects', v: top === undefined ? '…' : match ? String(match.count) : '0' },
            { k: t('seo.factPrice'), v: money(LEAD_PRICE) },
            { k: t('seo.factPost'), v: t('seo.factFree') },
          ]}
        />

        <LiveProjects state={st.code} city={city} limit={9} heading={t('seo.recentProjects', { place })} />

        <StateAlertForm st={st} city={city} />

        <Section title={es ? `Licencias para trabajar en ${city}` : `Licensing for work in ${city}`}>
          <p>{licenseNote(st, lang)}</p>
          <TrustStrip />
        </Section>

        {cityLeadLinks.length > 0 && (
          <LinkGrid title={es ? `Leads por oficio en ${place}` : `${place} leads by trade`} links={cityLeadLinks} icon="trade" />
        )}
        <LinkGrid title={es ? `Proyectos en ${city} por oficio` : `${city} projects by trade`} links={tradeLinks} icon="trade" />
        <LinkGrid title={es ? `Leads en todo ${stateName}` : `Statewide ${stateName} leads`} links={leadLinks} icon="trade" />

        <FaqSection faqs={faqs} />

        <TopCitiesSection st={st} exclude={city} title={es ? `Otras ciudades de ${stateName} con proyectos` : `Other top ${stateName} cities`} />

        <Link to={path(statePath(st))} className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-orange-600 hover:underline">
          {es ? `Todo ${stateName}` : `All of ${stateName}`} <ArrowRight className="h-4 w-4" />
        </Link>

        <CtaBand />
      </PageWrap>
    </>
  );
};

export default StateCityPage;
